import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react'

import { I18n } from 'aws-amplify/utils'
import { useTranslation } from 'react-i18next'
import {
  resolveLanguagePreference,
  setLanguageInStorage,
  type SupportedLanguage,
} from 'core/utils/language'

interface LanguageContextType {
  isKorean: boolean
  toggleLanguage: () => void
  setLanguage: (language: 'kr' | 'en') => void
  authKey: number
}

const LanguageContext = createContext<LanguageContextType>({
  isKorean: false,
  toggleLanguage: () => {},
  setLanguage: () => {},
  authKey: 0,
})

export const LanguageProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { i18n: reactI18n } = useTranslation()
  const [isKorean, setIsKorean] = useState(
    () => resolveLanguagePreference() === 'kr'
  )
  const authKey = 0

  const toggleLanguage = useCallback(() => {
    const newLanguage = isKorean ? 'en' : 'kr'
    try {
      I18n.setLanguage(newLanguage)
      reactI18n.changeLanguage(newLanguage)
      setLanguageInStorage(newLanguage)
    } catch (error) {
      console.error('Failed to change language:', error)
      return // Don't update state if external calls failed
    }
    setIsKorean(!isKorean)
  }, [isKorean, reactI18n])

  const setLanguage = useCallback((language: SupportedLanguage) => {
    const newIsKorean = language === 'kr'
    if (isKorean !== newIsKorean) {
      try {
        I18n.setLanguage(language)
        reactI18n.changeLanguage(language)
        setLanguageInStorage(language)
      } catch (error) {
        console.error('Failed to change language:', error)
        return // Don't update state if external calls failed
      }
      setIsKorean(newIsKorean)
    }
  }, [isKorean, reactI18n])

  const languageContextValue = useMemo(
    () => ({ isKorean, toggleLanguage, setLanguage, authKey }),
    [isKorean, toggleLanguage, setLanguage]
  )

  useEffect(() => {
    const language = resolveLanguagePreference()
    setLanguageInStorage(language)
    I18n.setLanguage(language)
    reactI18n.changeLanguage(language)
    setIsKorean(language === 'kr')
  }, [reactI18n])

  return (
    <LanguageContext.Provider value={languageContextValue}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => useContext(LanguageContext)
